import { getCookie, setCookie } from 'cookies-next/client'

import {
  type ContextData,
  type MetadataContent,
  type PlacementDecision,
} from '@redteclab/api/clients/adserver-api'
import { type ClientSessionCustomerData } from '@redteclab/api/clients/bully'

import { COOKIE_AD_SERVER_USER_ID_NAME } from '../cookies'
import { type PublicGlobalConfig } from '../global-config'
import { gtmDataLayerPushAdServerEvent } from '../gtm'
import { type TENANT } from '../tenant'
import { userDataGetAsuid } from '../user-data'

import {
  AD_BANNER_AUTO_REFRESH_CONFIG,
  AD_BANNER_AUTO_REFRESH_DATA_ATTR,
  AD_BANNER_HEIGHT_DATA_ATTR,
  AD_BANNER_LOCAL_STORAGE_USER_CLOSED_FLAG_KEY,
  AD_BANNER_PLACEHOLDER_CLASS_NAME,
  AD_BANNER_STATUS_DATA_ATTR,
  AD_BANNER_TAGS_DATA_ATTR,
  AD_BANNER_WIDTH_DATA_ATTR,
} from './adServerConst'
import {
  adServerCookieGetUserConsentForAdStorage,
  adServerCookiesGetOrGenerateUserIdentity,
} from './adServerCookie'
import {
  type AdServerCommand,
  type AdServerSetupPreloadedSlot,
  type AutoRefreshConfig,
} from './model/adServerTypes'

const adServerSlotsPush = (command: AdServerCommand): void => {
  globalThis._slots ??= []
  globalThis._slots.push(command)
}

export const adServerSlotsGetElements = (): HTMLElement[] => [
  ...document.querySelectorAll<HTMLElement>(
    `.${AD_BANNER_PLACEHOLDER_CLASS_NAME}`,
  ),
]

export const adServerSlotsPushSyncInterestCommand = (
  secondLevelCategories: string[],
): void => {
  adServerSlotsPush([
    'syncInterest',
    {
      visitedCategories: secondLevelCategories,
    },
  ])
}

export const adServerSlotPushSetupPreloadedSlotsCommand = (
  adServerSetupPreloadedSlots: AdServerSetupPreloadedSlot,
): void => {
  adServerSlotsPush(['setupPreloadedSlots', [adServerSetupPreloadedSlots]])
}

export const adServerSlotsPushSetupPreloadedSlotsCommand = (
  adServerSetupPreloadedSlots: AdServerSetupPreloadedSlot[],
): void => {
  adServerSlotsPush(['setupPreloadedSlots', adServerSetupPreloadedSlots])
}

export const adServerSlotsPushLoadCommand = (slots: Element[]): void => {
  adServerSlotsPush(['load', { slots }])
}

const adServerSlotsGetTargetingContext = (): ContextData => {
  return window._targeting.context
}

const adServerSlotsGetTargetingKeywords = (): string[] => {
  return window._targeting.keywords
}

export const adServerSlotsGetBannerSize = (
  slot: HTMLElement,
): { height: number; width: number } | null => {
  const widthString = slot.getAttribute(AD_BANNER_WIDTH_DATA_ATTR)
  const heightString = slot.getAttribute(AD_BANNER_HEIGHT_DATA_ATTR)
  if (widthString && heightString) {
    const width = Math.trunc(Number(widthString))
    const height = Math.trunc(Number(heightString))
    if (!Number.isNaN(width) && !Number.isNaN(height)) {
      return { height, width }
    }
  }

  return null
}

export const AD_SERVER_ON_PLACED_EVENT = 'adserveronplaced'
export const AD_SERVER_ON_REPLACED_EVENT = 'adserveronreplaced'
export const AD_SERVER_ON_PLACEMENTS_COUNT_SET_EVENT =
  'adserveronplacementscountset'

const onPlaced = (
  slot: HTMLElement,
  metadataContent: MetadataContent,
): void => {
  const event = new CustomEvent<MetadataContent>(AD_SERVER_ON_PLACED_EVENT, {
    detail: metadataContent,
  })

  slot.dispatchEvent(event)
}

const onReplaced = (slot: HTMLElement, currentIndex: number): void => {
  const event = new CustomEvent<number>(AD_SERVER_ON_REPLACED_EVENT, {
    detail: currentIndex,
  })

  slot.dispatchEvent(event)
}

const onPlacementsCountSet = (slot: HTMLElement, count: number): void => {
  const event = new CustomEvent<number>(
    AD_SERVER_ON_PLACEMENTS_COUNT_SET_EVENT,
    {
      detail: count,
    },
  )

  slot.dispatchEvent(event)
}

export const adServerSlotsGetAutoRefresh = (slot: HTMLElement): boolean => {
  return slot.getAttribute(AD_BANNER_AUTO_REFRESH_DATA_ATTR) === 'true'
}

export const adServerSlotsGetTags = (slot: HTMLElement): string | null =>
  slot.getAttribute(AD_BANNER_TAGS_DATA_ATTR)

export const adServerSlotsPushInitCommand = ({
  language,
  tenantAndEnv,
}: {
  language: PublicGlobalConfig['language']
  tenantAndEnv: PublicGlobalConfig['tenantAndEnv']
}): void => {
  const identity = adServerCookiesGetOrGenerateUserIdentity()

  adServerSlotsPush([
    'init',
    {
      cookieConsent: adServerCookieGetUserConsentForAdStorage(),
      getContext: adServerSlotsGetTargetingContext,
      getKeywords: adServerSlotsGetTargetingKeywords,

      getSlotAutoRefreshConfig: (
        slot: HTMLElement,
      ): AutoRefreshConfig | null => {
        const autoRefresh = adServerSlotsGetAutoRefresh(slot)

        if (autoRefresh) {
          return AD_BANNER_AUTO_REFRESH_CONFIG
        }

        return null
      },

      getSlotSize: adServerSlotsGetBannerSize,
      getSlotTags: (slot: HTMLElement): string[] | null | undefined =>
        adServerSlotsGetTags(slot)?.split(','),
      handleError: (): void => {
        // void
      },
      identity,
      language,

      mapPlacement: (
        slot: HTMLElement,
        placement: PlacementDecision | null,
      ): PlacementDecision | null => {
        if (!placement) {
          return null
        }

        const { flightId } = placement

        if (!flightId) {
          return null
        }

        let flightIds: string | null = null

        try {
          flightIds = localStorage.getItem(
            AD_BANNER_LOCAL_STORAGE_USER_CLOSED_FLAG_KEY,
          )
        } catch {
          flightIds = null
        }

        return flightIds?.includes(String(flightId)) ? null : placement
      },
      /**
       * if GTM `eventCallback` fails to fire the gtm event
       * It leads that the click URL might not work
       *  So as a fallback, there is a setTimeout to trigger click event
       */
      onClick: async (metadataContent: MetadataContent): Promise<void> => {
        return new Promise((resolve) => {
          if (Object.keys(metadataContent).length > 0) {
            gtmDataLayerPushAdServerEvent('kev_click', metadataContent, resolve)
          }
        })
      },

      onImpression: (metadataContent: MetadataContent): void => {
        if (Object.keys(metadataContent).length > 0) {
          gtmDataLayerPushAdServerEvent('kev_impr', metadataContent)
        }
      },

      onPlaced,
      onPlacementsCountSet,
      onReplaced,

      onStatusChange: (slot: HTMLElement, status: string): void => {
        slot.setAttribute(AD_BANNER_STATUS_DATA_ATTR, status)
      },
      onView: (metadataContent: MetadataContent): void => {
        if (Object.keys(metadataContent).length > 0) {
          gtmDataLayerPushAdServerEvent('kev_view', metadataContent)
        }
      },
      tenant: tenantAndEnv,
    },
  ])
}

export const adServerSyncInterestCommandIsPushed = (): boolean => {
  return (
    globalThis._slots?.some(
      ([commandName]) => commandName === 'syncInterest',
    ) ?? false
  )
}

export const adServerInitCommandIsPushed = (): boolean => {
  return (
    globalThis._slots?.some(([commandName]) => commandName === 'init') ?? false
  )
}

/**
 * whenever user logs in, we hash customer number and push to adserver client.
 */
export const adServerSlotsPushSyncUserCommand = (
  sessionData: ClientSessionCustomerData,
  tenant: TENANT,
): void => {
  const { customerNumber } = sessionData

  if (!customerNumber) {
    return
  }

  const asuid = userDataGetAsuid({ customerNumber, tenant })

  setCookie(COOKIE_AD_SERVER_USER_ID_NAME, asuid)

  const userId = getCookie(COOKIE_AD_SERVER_USER_ID_NAME)
  if (userId) {
    adServerSlotsPush([
      'syncUser',
      {
        customerNumber,
        tenant,
        userId,
      },
    ])
  }
}
